/**
 * Question figures per institution (Sep 11 2026): questions asked in the
 * trailing window, the share a mentor answered, and a weekly running total.
 *
 * Built on the suppression core, never changing it. The Worker builds its
 * snapshot with this (`src/worker/lib/questions-index.ts` reads the question
 * index) and the sample builds the demo with it, so the two cannot drift
 * apart.
 *
 * ── THE FLOOR ────────────────────────────────────────────────────────────
 *  - Asked in the window: a count of events, published only with ten
 *    students behind it (`eventFigure`).
 *  - Answered: a share of questions. It publishes only when the answered
 *    questions and the open ones each have ten students behind them, or none
 *    at all, because the share and the total together publish both parts.
 *  - The weekly running total publishes a week only with ten students behind it.
 */

import { count, suppressed, unavailable, value, type Cell, type Rate } from "~/lib/suppression";
import { eventFigure, type EventFigureCells } from "~/lib/event-figure";
import { ACTIVITY_WINDOW_DAYS, activityWindow } from "~/lib/app-use";
import { previousSunday } from "~/app/lib/format";
import type { WeeklyPoint, WeeklySeries } from "~/worker/lib/api-types";

/** One question, as the builder needs it. Internal: never serialized. */
export interface QuestionRecord {
  readonly id: string;
  readonly student: string;
  readonly at: number;
  readonly answered: boolean;
}

export interface QuestionCells {
  readonly window: { readonly start: string; readonly end: string; readonly days: number };
  /** Questions asked in the window, and the students who asked them. */
  readonly asked: EventFigureCells;
  /** Of every question the students have asked, the share a mentor answered, with both counts. */
  readonly answered: Cell<Rate>;
  /** Questions asked, a running total by week. */
  readonly weekly: WeeklySeries;
}

const WEEKS_SHOWN = 12;
const DAY_MS = 86_400_000;

/** One institution's question figures. `students` are its claimed students' ids. */
export function questionCells(
  students: readonly string[],
  questions: readonly QuestionRecord[],
  floor: number,
  nowMs: number,
): QuestionCells {
  const claimed = new Set(students);
  const mine = questions.filter((q) => claimed.has(q.student));
  const today = Math.floor(nowMs / DAY_MS) * DAY_MS;
  const inWindow = mine.filter((q) => q.at >= today - ACTIVITY_WINDOW_DAYS * DAY_MS && q.at < today);
  return {
    window: { ...activityWindow(nowMs), days: ACTIVITY_WINDOW_DAYS },
    asked: eventFigure(inWindow.length, studentsBehind(inWindow), claimed.size, floor),
    answered: answeredShare(mine, floor),
    weekly: weeklyRunningTotal(mine, floor, nowMs),
  };
}

function studentsBehind(events: readonly { readonly student: string }[]): number {
  return new Set(events.map((e) => e.student)).size;
}

function answeredShare(mine: readonly QuestionRecord[], floor: number): Cell<Rate> {
  if (mine.length === 0) return unavailable("no questions asked yet");
  const answered = mine.filter((q) => q.answered);
  const open = mine.filter((q) => !q.answered);
  const behindAnswered = studentsBehind(answered);
  const behindOpen = studentsBehind(open);
  if (studentsBehind(mine) < floor || (behindAnswered > 0 && behindAnswered < floor)) return suppressed("below-threshold");
  if (behindOpen > 0 && behindOpen < floor) return suppressed("complementary");
  return value({ numerator: answered.length, denominator: mine.length, rate: answered.length / mine.length });
}

/**
 * A running total of events by week, Sundays, the current partial week left
 * out, as the claims and milestones curves are drawn. A week publishes only
 * once ten students stand behind its total; a real zero publishes as zero.
 */
export function weeklyRunningTotal(
  events: readonly { readonly student: string; readonly at: number }[],
  floor: number,
  nowMs: number,
): WeeklySeries {
  const thisSunday = previousSunday(nowMs);
  const points: WeeklyPoint[] = [];
  for (let i = WEEKS_SHOWN - 1; i >= 0; i--) {
    const end = new Date(thisSunday);
    end.setUTCDate(end.getUTCDate() - i * 7);
    const upTo = events.filter((e) => e.at <= end.getTime());
    points.push({
      weekEnding: end.toISOString().slice(0, 10),
      cumulative:
        upTo.length === 0 ? count(0) : studentsBehind(upTo) >= floor ? count(upTo.length) : suppressed("below-threshold"),
    });
  }
  return { anchor: "sunday", partialWeekExcluded: true, points };
}
